import { useEffect, useRef } from "react";

declare global {
  interface Window {
    __TURNSTILE_SITE_KEY__?: string;
    turnstile?: {
      render: (
        container: string | HTMLElement,
        params: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "invisible";
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

// Renders Cloudflare Turnstile and pipes the resolved token back up via
// `onToken`. When TURNSTILE_SITE_KEY isn't configured (no script, no global),
// the widget renders nothing and the parent form is expected to treat token
// as unnecessary — `isConfigured()` lets the parent know.
export function isTurnstileConfigured(): boolean {
  return typeof window !== "undefined" && Boolean(window.__TURNSTILE_SITE_KEY__);
}

interface TurnstileWidgetProps {
  onToken: (token: string) => void;
  onExpired?: () => void;
}

export function TurnstileWidget({ onToken, onExpired }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isTurnstileConfigured()) return;
    const siteKey = window.__TURNSTILE_SITE_KEY__!;

    let cancelled = false;
    const renderWhenReady = () => {
      if (cancelled) return;
      if (!window.turnstile || !containerRef.current) {
        setTimeout(renderWhenReady, 100);
        return;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: onToken,
        "expired-callback": () => onExpired?.(),
        theme: "auto",
      });
    };
    renderWhenReady();

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore — widget already gone
        }
        widgetIdRef.current = null;
      }
    };
  }, [onToken, onExpired]);

  if (!isTurnstileConfigured()) return null;
  return <div ref={containerRef} className="my-2" />;
}
